import { useMemo, useState, useEffect } from 'react';
import {
  Building2, Mail, Check, ChevronRight, ChevronLeft, Loader2, Copy, Sparkles, CreditCard, CheckCircle2, FileText, Info,
} from 'lucide-react';
import * as apiClient from '@/services/apiClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

const STEPS = [
  { id: 1, title: 'Company', icon: Building2 },
  { id: 2, title: 'Admin', icon: Mail },
  { id: 3, title: 'Terms', icon: CreditCard },
  { id: 4, title: 'Billing', icon: FileText },
  { id: 5, title: 'Review', icon: Check },
];

const CURRENCY_SYMBOL = '₹';

const PLAN_ACCENT = {
  starter: 'border-slate-500 hover:border-slate-300',
  professional: 'border-teal-500 hover:border-teal-300',
  growth: 'border-teal-500 hover:border-teal-300',
  scale: 'border-purple-500 hover:border-purple-300',
};

const PLAN_SELECTED_BG = {
  starter: 'bg-muted border-foreground/40',
  professional: 'bg-primary/10 border-primary',
  growth: 'bg-primary/10 border-primary',
  scale: 'bg-purple-500/10 border-purple-500',
};

function slugifyId(name) {
  return name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 48);
}

const EMPTY_FORM = {
  companyId: '', companyName: '', contactEmail: '', description: '',
  planId: '', planSlug: '', enableTrial: true,
  // Trial length and grace window are per-deal decisions now, not constants.
  trialDays: 14, gracePeriodDays: 7,
  // Billing identity, captured up front where known so the first invoice does
  // not stall waiting for it. All optional at this stage.
  billingLegalName: '', billingGstin: '', billingPan: '',
  billingAddress1: '', billingAddress2: '', billingCity: '',
  billingState: '', billingPincode: '', billingEmail: '',
  // Optional profile seed — whatever is already known from the sales
  // conversation, so the company does not start from a blank profile.
  website: '', industry: '', companySize: '', location: '',
  adminFirstName: '', adminLastName: '',
};

// The id is normally derived as you type the name. A seeded form never fires
// that, so derive it here too — otherwise step 1 can never be completed.
function seedForm(initialValues) {
  const seeded = { ...EMPTY_FORM, ...initialValues };
  if (!seeded.companyId && seeded.companyName) seeded.companyId = slugifyId(seeded.companyName);
  return seeded;
}

/**
 * @param {object} [initialValues]  Seeds the form — used when onboarding a lead,
 *                                  so the details already captured are not retyped.
 */
export default function CreateCompanyWizard({ open, onOpenChange, onCreated, initialValues }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [plansLoading, setPlansLoading] = useState(false);
  const [error, setError] = useState('');
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState(() => seedForm(initialValues));
  const [idTouched, setIdTouched] = useState(false);

  // Re-seed each time it opens: the lead being onboarded can differ between
  // openings, and the component is not remounted in between.
  useEffect(() => {
    if (open) {
      setForm(seedForm(initialValues));
      setStep(1);
      setError('');
      setIdTouched(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open && step === 3 && plans.length === 0) {
      setPlansLoading(true);
      apiClient.getActivePlans()
        .then(res => setPlans(res.data || []))
        .catch(() => {})
        .finally(() => setPlansLoading(false));
    }
  }, [open, step]);

  const reset = () => {
    setStep(1); setError('');
    setForm(seedForm(initialValues));
    setIdTouched(false); setPlans([]);
  };

  const handleClose = (o) => { if (!o && !loading) reset(); onOpenChange(o); };

  const update = (field, value) => {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'companyName' && !idTouched) next.companyId = slugifyId(value);
      return next;
    });
  };

  // Clicking the selected plan again clears it. A trial can start with no plan
  // at all, so there has to be a way back to "none" once one is picked.
  const selectPlan = (plan) => setForm(f => (
    f.planId === plan._id
      ? { ...f, planId: '', planSlug: '' }
      : { ...f, planId: plan._id, planSlug: plan.slug }
  ));
  const clearPlan = () => setForm(f => ({ ...f, planId: '', planSlug: '' }));

  const canStep1 = form.companyId.trim().length >= 2 && form.companyName.trim().length >= 2;
  const canStep2 = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail.trim());
  // A plan is only required when going straight to paid. A trial can start
  // without one and run on the Trial plan's limits, so a prospect can be
  // onboarded before the commercials are settled.
  const canStep3 = form.enableTrial ? Number(form.trialDays) > 0 : !!form.planId;
  // Billing is optional here — it blocks invoicing later, not onboarding now.
  const canStep4 = !form.billingEmail.trim()
    || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.billingEmail.trim());

  const stepValid = useMemo(() => {
    if (step === 1) return canStep1;
    if (step === 2) return canStep2;
    if (step === 3) return canStep3;
    if (step === 4) return canStep4;
    return canStep1 && canStep2 && canStep3 && canStep4;
  }, [step, canStep1, canStep2, canStep3, canStep4]);

  const handleNext = () => { setError(''); if (step < 5) setStep(s => s + 1); };
  const handleBack = () => { setError(''); if (step > 1) setStep(s => s - 1); };

  const handleSubmit = async () => {
    if (!stepValid) return;
    setLoading(true); setError('');
    try {
      const response = await apiClient.createCompany({
        companyId: form.companyId.trim(),
        companyName: form.companyName.trim(),
        contactEmail: form.contactEmail.trim(),
        description: form.description.trim(),
        planId: form.planId || null,
        enableTrial: form.enableTrial,
        trialDays: form.enableTrial ? Number(form.trialDays) : undefined,
        gracePeriodDays: Number(form.gracePeriodDays),
        billing: {
          legalName:    form.billingLegalName.trim(),
          gstin:        form.billingGstin.trim().toUpperCase(),
          pan:          form.billingPan.trim().toUpperCase(),
          addressLine1: form.billingAddress1.trim(),
          addressLine2: form.billingAddress2.trim(),
          city:         form.billingCity.trim(),
          state:        form.billingState.trim(),
          pincode:      form.billingPincode.trim(),
          billingEmail: form.billingEmail.trim(),
        },
        adminFirstName: form.adminFirstName.trim(),
        adminLastName:  form.adminLastName.trim(),
        profile: {
          website:     form.website.trim(),
          industry:    form.industry.trim(),
          companySize: form.companySize,
          location:    form.location.trim(),
        },
      });
      onCreated?.({
        message: response.message,
        email: response.data?.adminUser?.email,
        password: '12345678',
        // Needed by callers that have to link the new company to something
        // else — the lead pipeline marks the lead onboarded with it.
        companyId: form.companyId.trim(),
        companyName: form.companyName.trim(),
        plan: response.data?.plan?.name,
      });
      handleClose(false);
    } catch (err) {
      setError(err.message || 'Failed to create company.');
    } finally {
      setLoading(false);
    }
  };

  const selectedPlan = plans.find(p => p._id === form.planId);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="gap-0 overflow-hidden p-0 sm:max-w-2xl">
        <DialogHeader className="space-y-0 border-b border-border bg-muted/30 px-5 py-4">
          <DialogTitle className="flex items-center gap-2 text-base font-semibold">
            <Sparkles className="h-4 w-4 text-primary" aria-hidden />
            Onboard new company
          </DialogTitle>
          <p className="text-xs text-muted-foreground">Four quick steps — admin account and subscription created automatically.</p>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex border-b border-border bg-card px-5 py-3">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const active = step === s.id;
            const done = step > s.id;
            return (
              <div key={s.id} className="flex flex-1 items-center">
                <div className={cn('flex items-center gap-1.5 text-xs font-medium transition-colors', active && 'text-primary', done && 'text-foreground', !active && !done && 'text-muted-foreground')}>
                  <span className={cn('flex h-7 w-7 items-center justify-center rounded-full border text-[11px]', active && 'border-primary bg-primary text-primary-foreground', done && 'border-primary/40 bg-primary/15 text-primary', !active && !done && 'border-border bg-muted/40')}>
                    {done ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
                  </span>
                  <span className="hidden sm:inline">{s.title}</span>
                </div>
                {i < STEPS.length - 1 && <ChevronRight className="mx-1 h-4 w-4 shrink-0 text-muted-foreground/40" />}
              </div>
            );
          })}
        </div>

        <div className="space-y-4 px-5 py-5 max-h-[60vh] overflow-y-auto">
          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>
          )}

          {step === 1 && (
            <div className="space-y-4 animate-in fade-in duration-200">
              <div>
                <Label htmlFor="co-name">Company name</Label>
                <Input id="co-name" className="mt-1.5 h-10" placeholder="Acme Corp" value={form.companyName} onChange={e => update('companyName', e.target.value)} autoFocus />
              </div>
              <div>
                <Label htmlFor="co-id">Company ID (URL-safe)</Label>
                <Input id="co-id" className="mt-1.5 h-10 font-mono text-sm" placeholder="acme-corp" value={form.companyId} onChange={e => { setIdTouched(true); update('companyId', e.target.value); }} />
                <p className="mt-1 text-[11px] text-muted-foreground">Used for tenant isolation. Auto-filled from name.</p>
              </div>

              <div className="rounded-lg border border-border bg-muted/20 p-3">
                <p className="text-xs font-medium text-foreground">Company details <span className="font-normal text-muted-foreground">— optional</span></p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  Anything you fill in here appears on their public job pages. They can edit it later.
                </p>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="co-website" className="text-xs">Website</Label>
                    <Input id="co-website" className="mt-1 h-9 text-sm" placeholder="acme.com"
                      value={form.website} onChange={e => update('website', e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="co-industry" className="text-xs">Industry</Label>
                    <Input id="co-industry" className="mt-1 h-9 text-sm" placeholder="SaaS"
                      value={form.industry} onChange={e => update('industry', e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="co-size" className="text-xs">Company size</Label>
                    <select id="co-size" value={form.companySize} onChange={e => update('companySize', e.target.value)}
                      className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
                      <option value="">Not specified</option>
                      {['1-10','11-50','51-200','201-500','501-1000','1000+'].map(v => <option key={v} value={v}>{v} employees</option>)}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="co-location" className="text-xs">Location</Label>
                    <Input id="co-location" className="mt-1 h-9 text-sm" placeholder="Bengaluru, India"
                      value={form.location} onChange={e => update('location', e.target.value)} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4 animate-in fade-in duration-200">
              <div>
                <Label htmlFor="co-email">Admin email</Label>
                <Input id="co-email" type="email" className="mt-1.5 h-10" placeholder="admin@company.com" value={form.contactEmail} onChange={e => update('contactEmail', e.target.value)} autoFocus />
                <p className="mt-1 text-[11px] text-muted-foreground">Default password: <span className="font-mono font-medium">12345678</span> (share securely after create)</p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="co-admin-first">Admin first name</Label>
                  <Input id="co-admin-first" className="mt-1.5 h-10" placeholder="Priya"
                    value={form.adminFirstName} onChange={e => update('adminFirstName', e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="co-admin-last">Admin last name</Label>
                  <Input id="co-admin-last" className="mt-1.5 h-10" placeholder="Sharma"
                    value={form.adminLastName} onChange={e => update('adminLastName', e.target.value)} />
                </div>
              </div>
              <p className="-mt-1 text-[11px] text-muted-foreground">
                Left blank, the name is guessed from the email address — which reads poorly
                for shared inboxes like careers@.
              </p>
              <div>
                <Label htmlFor="co-desc">Description (optional)</Label>
                <textarea id="co-desc" rows={3} className="mt-1.5 flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none" placeholder="Industry, size, or notes" value={form.description} onChange={e => update('description', e.target.value)} />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4 animate-in fade-in duration-200">
              <div>
                <p className="text-sm font-semibold text-foreground mb-1">Select a Pricing Plan</p>
                <p className="text-xs text-muted-foreground mb-4">This determines the company&apos;s feature limits and interview quotas.</p>

                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs text-muted-foreground">
                    {form.planId
                      ? 'Click the selected plan again to clear it.'
                      : form.enableTrial
                        ? 'No plan selected — the trial will run on the Trial plan\u2019s limits.'
                        : 'Select a plan, or turn on the trial below to start without one.'}
                  </p>
                  {form.planId && (
                    <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={clearPlan}>
                      Clear selection
                    </Button>
                  )}
                </div>

                {plansLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading plans...</div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {plans.map(plan => {
                      const selected = form.planId === plan._id;
                      const accent = selected ? (PLAN_SELECTED_BG[plan.slug] || 'bg-primary/10 border-primary') : (PLAN_ACCENT[plan.slug] || 'border-border hover:border-foreground/40');
                      return (
                        <button key={plan._id} onClick={() => selectPlan(plan)} className={cn('text-left rounded-xl border-2 p-4 transition-all cursor-pointer', accent)}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-sm">{plan.name}</span>
                            <div className="flex items-center gap-1.5">
                              {plan.isPopular && <span className="text-[10px] font-bold bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full">POPULAR</span>}
                              {selected && <CheckCircle2 size={16} className="text-primary" />}
                            </div>
                          </div>
                          <p className="text-lg font-bold">{CURRENCY_SYMBOL}{plan.price.toLocaleString('en-IN')}<span className="text-xs font-normal text-muted-foreground"> /mo</span></p>
                          <p className="text-xs text-muted-foreground mt-1">{plan.description}</p>
                          <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 mt-3 text-xs text-muted-foreground">
                            <span>Jobs: <b className="text-foreground">{plan.limits?.maxActiveJobs === -1 ? '∞' : plan.limits?.maxActiveJobs}</b></span>
                            <span>Interviews: <b className="text-foreground">{plan.limits?.maxInterviewsPerMonth === -1 ? '∞' : plan.limits?.maxInterviewsPerMonth}/mo</b></span>
                            <span>Users: <b className="text-foreground">{plan.limits?.maxUsers === -1 ? '∞' : plan.limits?.maxUsers}</b></span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Trial terms */}
              <div className="space-y-3 rounded-lg border border-border bg-muted/20 p-3">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="trial-toggle"
                    checked={form.enableTrial}
                    onChange={e => update('enableTrial', e.target.checked)}
                    className="mt-0.5 accent-teal-500"
                  />
                  <label htmlFor="trial-toggle" className="cursor-pointer">
                    <p className="text-sm font-medium">Start with a free trial</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {form.planId
                        ? 'The company runs on the selected plan\u2019s limits and is not billed until the trial ends.'
                        : 'With no plan selected, the trial runs on the Trial plan\u2019s limits. Pick a plan later when the deal is agreed.'}
                    </p>
                  </label>
                </div>

                {form.enableTrial && (
                  <div className="grid grid-cols-2 gap-3 pl-7">
                    <div>
                      <label htmlFor="trial-days" className="text-xs font-medium text-muted-foreground">Trial length (days)</label>
                      <Input
                        id="trial-days" type="number" min={1} max={365}
                        value={form.trialDays}
                        onChange={e => update('trialDays', e.target.value)}
                        className="mt-1 h-9"
                      />
                    </div>
                    <div>
                      <label htmlFor="grace-days" className="text-xs font-medium text-muted-foreground">Grace after trial (days)</label>
                      <Input
                        id="grace-days" type="number" min={0} max={90}
                        value={form.gracePeriodDays}
                        onChange={e => update('gracePeriodDays', e.target.value)}
                        className="mt-1 h-9"
                      />
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        Time to pay before new work is blocked.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4 animate-in fade-in duration-200">
              <p className="flex items-start gap-2 rounded-lg border border-border bg-muted/20 p-3 text-xs text-muted-foreground">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  All optional. Missing details block the first invoice, not this
                  onboarding — you can fill them in when the deal closes.
                </span>
              </p>

              <div>
                <label htmlFor="b-legal" className="text-sm font-medium">Registered legal name</label>
                <Input id="b-legal" value={form.billingLegalName}
                  onChange={e => update('billingLegalName', e.target.value)}
                  placeholder="Acme Technologies Private Limited" className="mt-1.5" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="b-gstin" className="text-sm font-medium">GSTIN</label>
                  <Input id="b-gstin" value={form.billingGstin}
                    onChange={e => update('billingGstin', e.target.value.toUpperCase())}
                    placeholder="27AAPFU0939F1ZV" maxLength={15} className="mt-1.5 font-mono text-sm" />
                  <p className="mt-1 text-[11px] text-muted-foreground">Sets the tax treatment on their invoices.</p>
                </div>
                <div>
                  <label htmlFor="b-pan" className="text-sm font-medium">PAN</label>
                  <Input id="b-pan" value={form.billingPan}
                    onChange={e => update('billingPan', e.target.value.toUpperCase())}
                    placeholder="AAPFU0939F" maxLength={10} className="mt-1.5 font-mono text-sm" />
                </div>
              </div>

              <div>
                <label htmlFor="b-addr1" className="text-sm font-medium">Registered address</label>
                <Input id="b-addr1" value={form.billingAddress1}
                  onChange={e => update('billingAddress1', e.target.value)}
                  placeholder="Address line 1" className="mt-1.5" />
                <Input value={form.billingAddress2}
                  onChange={e => update('billingAddress2', e.target.value)}
                  placeholder="Address line 2 (optional)" className="mt-2" />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label htmlFor="b-city" className="text-sm font-medium">City</label>
                  <Input id="b-city" value={form.billingCity}
                    onChange={e => update('billingCity', e.target.value)} className="mt-1.5" />
                </div>
                <div>
                  <label htmlFor="b-state" className="text-sm font-medium">State</label>
                  <Input id="b-state" value={form.billingState}
                    onChange={e => update('billingState', e.target.value)} className="mt-1.5" />
                </div>
                <div>
                  <label htmlFor="b-pin" className="text-sm font-medium">Pincode</label>
                  <Input id="b-pin" value={form.billingPincode}
                    onChange={e => update('billingPincode', e.target.value)} className="mt-1.5" />
                </div>
              </div>

              <div>
                <label htmlFor="b-email" className="text-sm font-medium">Billing email</label>
                <Input id="b-email" type="email" value={form.billingEmail}
                  onChange={e => update('billingEmail', e.target.value)}
                  placeholder="finance@acme.com" className="mt-1.5" />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Where invoices go. Needs verifying before the first one is sent — rarely the same as the admin above.
                </p>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-3 rounded-lg border border-border bg-muted/20 p-4 animate-in fade-in duration-200">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Summary</p>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">Company</dt>
                  <dd className="font-medium">{form.companyName}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">ID</dt>
                  <dd className="font-mono text-xs">{form.companyId}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">Admin email</dt>
                  <dd className="font-medium">{form.contactEmail}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">Plan</dt>
                  <dd className="font-semibold text-teal-500">
                    {selectedPlan
                      ? `${selectedPlan.name} — ${CURRENCY_SYMBOL}${selectedPlan.price?.toLocaleString('en-IN')}/mo`
                      : 'None yet — Trial plan limits apply'}
                  </dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">Trial</dt>
                  <dd>{form.enableTrial ? `${form.trialDays}-day free trial` : 'No trial — active immediately'}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">Grace period</dt>
                  <dd>{form.gracePeriodDays} days</dd>
                </div>
                {form.billingEmail && (
                  <div className="flex justify-between gap-2">
                    <dt className="text-muted-foreground">Billing email</dt>
                    <dd>{form.billingEmail}</dd>
                  </div>
                )}
                {form.billingGstin && (
                  <div className="flex justify-between gap-2">
                    <dt className="text-muted-foreground">GSTIN</dt>
                    <dd className="font-mono text-xs">{form.billingGstin}</dd>
                  </div>
                )}
                {form.description && (
                  <div>
                    <dt className="text-muted-foreground">Description</dt>
                    <dd className="mt-1">{form.description}</dd>
                  </div>
                )}
              </dl>
            </div>
          )}
        </div>

        <DialogFooter className="flex-row gap-2 border-t border-border bg-muted/20 px-5 py-4 sm:justify-between">
          <Button type="button" variant="ghost" size="sm" disabled={loading || step === 1} onClick={handleBack}>
            <ChevronLeft className="mr-1 h-4 w-4" /> Back
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" disabled={loading} onClick={() => handleClose(false)}>Cancel</Button>
            {step < 5 ? (
              <Button type="button" size="sm" disabled={!stepValid} onClick={handleNext}>
                Continue <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            ) : (
              <Button type="button" size="sm" disabled={loading || !stepValid} className="gap-2" onClick={handleSubmit}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                {loading ? 'Creating…' : 'Create company'}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CredentialsBanner({ credentials, onDismiss }) {
  if (!credentials) return null;

  const copyAll = () => {
    navigator.clipboard?.writeText(`Email: ${credentials.email}\nPassword: ${credentials.password}`);
  };

  return (
    <div className="mb-4 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3">
      <p className="mb-2 text-sm font-semibold text-foreground">{credentials.message}</p>
      {credentials.plan && (
        <p className="text-xs text-muted-foreground mb-2">Plan: <strong>{credentials.plan}</strong></p>
      )}
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="text-muted-foreground">Email: <strong className="text-foreground">{credentials.email}</strong></span>
        <span className="text-muted-foreground">Password: <strong className="font-mono text-foreground">{credentials.password}</strong></span>
        <Button type="button" variant="outline" size="sm" className="h-7 gap-1 text-xs" onClick={copyAll}>
          <Copy className="h-3 w-3" /> Copy
        </Button>
        <Button type="button" variant="ghost" size="sm" className="ml-auto h-7 text-xs" onClick={onDismiss}>Dismiss</Button>
      </div>
    </div>
  );
}
